package no.vareflow.web

import no.vareflow.app.core.CloudContext
import no.vareflow.app.core.VareflowCore
import no.vareflow.app.data.local.BrowserKeyValueStore
import no.vareflow.app.domain.*

enum class Tab(private val nb: String, private val en: String) {
    OVERVIEW("Oversikt", "Overview"), TELLING("Telling", "Count"), ANALYSE("Analyse", "Analysis"), PRODUCTS("Produkter", "Products"), MORE("Mer", "More");

    val label: String get() = t(nb, en)
}

/** A modal dialog: [build] draws the body and the buttons; it is redrawn with the page. */
class Dialog(val title: String, val build: El.(close: () -> Unit) -> Unit)

/**
 * The web app: holds what is on screen (signed-in user, tab, view state, open dialog) and redraws
 * the page from [core] whenever something changes. The phone's screens are the reference for
 * texts, rules and order of elements.
 */
class WebApp(private val localCore: VareflowCore, private val root: El) {
    /** Local test data, or after a Firebase login the signed-in company's data (cloud mode). */
    var core: VareflowCore = localCore
        private set
    val cloud = WebCloud(onChange = { requestRender() }, onSignedOut = { message -> cloudEnded(message) })
    val cloudMode: Boolean get() = core.cloud != null
    /** A remembered Firebase login is being restored after a reload. */
    var restoringCloud = false
        private set
    var user: UserAccount? = null
        private set
    var tab = Tab.OVERVIEW
    var dialog: Dialog? = null
    private var toastText: String? = null
    private var toastTimer = -1
    private var renderQueued = false
    var loginError: String? = null

    // View state of each screen.
    var countCategory: String? = null
    var countSearch = ""
    var productsCategory: String? = null
    var productsSearch = ""
    var moreView = "main"
    /** Mer → Svinn: the month shown. */
    var svinnMonth: String? = null
    var auditSearch = ""
    /** Analyse page sub-view: "analyse" (live) or "historikk" (closed months). */
    var analyseView = "analyse"
    /** The owner's own page (Garasjestudio) is shown instead of the company's tabs. */
    var ownerView = false
    /** Set at login: the owner lands on the Eier page as soon as the owner check has answered. */
    var openOwnerHomeOnce = false

    /** The open Vareflow dropdown (selectBox key), or null. */
    var openSelect: String? = null

    /** "Moderne visning" test switch (Mer → Visning), remembered in this browser. */
    var modernUi = loadModernUi()
    /** Last values drawn in each progress ring, so rings only animate when the numbers change. */
    val lastRings = mutableMapOf<String, Pair<Int, Int>>()

    /** Count edits typed but not yet saved (saved after a short pause, like the phone). */
    val pendingCounts = mutableMapOf<String, PendingCount>()

    class PendingCount(val sessionId: String, val productId: String, val departmentId: String, var text: String, var timer: Int)

    init {
        I18n.language = loadLanguagePref()
        localCore.addListener { requestRender() }
        user = if (webTestMode) localCore.rememberedUser() else null
        window.setInterval({ tick() }, 1000)
        // A click outside an open dropdown closes it.
        document.addEventListener("click", { e: dynamic ->
            if (openSelect != null) {
                val target = e.target
                val inside = target != null && jsTypeOf(target.closest) == "function" && target.closest(".select-wrap") != null
                if (!inside) { openSelect = null; requestRender() }
            }
        })
        window.addEventListener("beforeunload", { flushPendingCounts() })
        // Besøksteller (Eier-siden): one anonymous page view per tab.
        if (cloud.available) cloud.recordVisit(login = false)
        if (user == null && cloud.available && cloud.lastCompanyId().isNotBlank()) {
            restoringCloud = true
            cloud.restore { result ->
                restoringCloud = false
                result.onSuccess { activateCloud(it, remember = cloud.isRemembered()) }
                    .onFailure { e -> e.message?.takeIf { it.isNotBlank() }?.let { loginError = it } }
                requestRender()
            }
        }
    }

    /** After a Firebase login: show the company's data (cached in this browser, kept live by [cloud]). */
    fun activateCloud(membership: WebMembership, remember: Boolean) {
        val companyCore = VareflowCore(
            BrowserKeyValueStore(cloudStorePrefix(membership)), WEB_VERSION, "web-cloud",
            cloud = CloudContext(membership.companyId, membership.locationId, membership.locationName)
        )
        companyCore.addListener { requestRender() }
        core = companyCore
        cloud.setRemember(remember)
        cloud.attach(companyCore)
        localCore.setRemembered(null, false)
        // The language follows the account: use the saved one, or save the one chosen on the login page.
        cloud.loadLanguage { saved ->
            if (saved == null) cloud.saveLanguage(I18n.language)
            else if (saved != I18n.language) setLanguage(saved, saveToAccount = false)
        }
        user = companyCore.userById(membership.uid)
        loginError = null
        tab = Tab.OVERVIEW
        moreView = "main"
        countCategory = null
        productsCategory = null
        requestRender()
    }

    fun currentUser(): UserAccount? = user?.let { core.userById(it.id) }

    /** The Eier page (owner only), with a fresh company list. */
    fun openOwnerPage() {
        flushPendingCounts()
        ownerView = true
        dialog = null
        loadOwnerCompanies()
        loadOwnerVisits()
        window.scrollTo(0, 0)
        requestRender()
    }

    /** Switches the UI language; in Firebase mode it is also saved on the person's account. */
    fun setLanguage(language: AppLanguage, saveToAccount: Boolean = true) {
        I18n.language = language
        saveLanguagePref(language)
        if (saveToAccount && cloudMode) cloud.saveLanguage(language)
        requestRender()
    }

    /** The cloud session ended without a logout here (e.g. deactivated): back to the login page. */
    private fun cloudEnded(message: String) {
        pendingCounts.clear()
        core = localCore
        user = null
        dialog = null
        moreView = "main"
        loginError = message
        requestRender()
    }

    fun login(found: UserAccount, remember: Boolean) {
        cloud.forgetSession()
        core.setRemembered(found, remember)
        user = found
        loginError = null
        tab = Tab.OVERVIEW
        requestRender()
    }

    fun logout(message: String? = null) {
        flushPendingCounts()
        if (cloudMode) {
            cloud.signOut()
            core = localCore
        }
        core.setRemembered(null, false)
        user = null
        dialog = null
        moreView = "main"
        ownerView = false
        openOwnerHomeOnce = false
        loginError = message
        requestRender()
    }

    fun select(newTab: Tab) {
        flushPendingCounts()
        // Tapping a tab again goes back to its start page.
        if (newTab == Tab.MORE) moreView = "main"
        if (newTab == Tab.PRODUCTS && tab == Tab.PRODUCTS) productsCategory = null
        ownerView = false
        tab = newTab
        dialog = null
        window.scrollTo(0, 0)
        requestRender()
    }

    fun toast(text: String) {
        toastText = text
        if (toastTimer >= 0) clearTimeout(toastTimer)
        toastTimer = setTimeout(3200) { toastText = null; requestRender() }
        requestRender()
    }

    fun openDialog(title: String, build: El.(close: () -> Unit) -> Unit) {
        openSelect = null
        dialog = Dialog(title, build)
        requestRender()
    }

    fun closeDialog() {
        dialog = null
        openSelect = null
        requestRender()
    }

    fun requestRender() {
        if (renderQueued) return
        renderQueued = true
        window.requestAnimationFrame { renderQueued = false; render() }
    }

    // ---------------------------------------------------------------- counting edits

    fun pendingKey(sessionId: String, productId: String, departmentId: String) = "$sessionId|$productId|$departmentId"

    fun editCount(sessionId: String, productId: String, departmentId: String, text: String) {
        val key = pendingKey(sessionId, productId, departmentId)
        val pending = pendingCounts[key]
        if (pending != null) {
            clearTimeout(pending.timer)
            pending.text = text
            pending.timer = setTimeout(COUNT_SAVE_DEBOUNCE_MS) { commitCount(key) }
        } else {
            pendingCounts[key] = PendingCount(sessionId, productId, departmentId, text, setTimeout(COUNT_SAVE_DEBOUNCE_MS) { commitCount(key) })
        }
    }

    fun commitCount(key: String) {
        val pending = pendingCounts.remove(key) ?: return
        clearTimeout(pending.timer)
        val u = currentUser() ?: return
        val text = pending.text.trim()
        val parsed = if (text.isEmpty()) null else parseQuantity(text)
        if (text.isNotEmpty() && parsed == null) { requestRender(); return }
        val existing = core.entry(pending.sessionId, pending.productId, pending.departmentId)
        if (existing?.quantity == parsed) { requestRender(); return }
        val product = core.products.firstOrNull { it.id == pending.productId } ?: return
        if (!core.canEditCategory(pending.sessionId, product.category, u)) return
        core.saveCountEntry(pending.sessionId, pending.productId, pending.departmentId, parsed, existing?.note.orEmpty(), u.id)
    }

    fun flushPendingCounts() {
        pendingCounts.keys.toList().forEach { commitCount(it) }
    }

    // ---------------------------------------------------------------- drawing

    private fun tick() {
        val u = currentUser() ?: return
        // Time-limited helper in the cloud: log out when the time is over (the rules refuse
        // everything after it anyway), and keep the header countdown fresh.
        if (cloudMode) {
            val until = cloud.membership?.accessUntil ?: return
            if (HelperAccess.isExpired(until, nowMillis())) {
                logout(HelperAccess.expiredMessage)
                return
            }
            byId("access-timer")?.text(helperPillText(until))
            return
        }
        // The opening-hours timer belongs to local test mode (cloud access is set by memberships).
        if (u.role == Role.EMPLOYEE && !cloudMode) {
            val remaining = (u.accessUntil ?: 0L) - nowMillis()
            if (remaining <= 0) {
                logout(t("Tilgangen er stengt. Be en admin åpne varetellingen.", "Access is closed. Ask an admin to open the stock count."))
                return
            }
            byId("access-timer")?.text(accessText(remaining))
        }
    }

    private fun helperPillText(until: Long): String =
        t("Tilgang: ${HelperAccess.remainingText(until, nowMillis())}", "Access: ${HelperAccess.remainingText(until, nowMillis())}")

    private fun accessText(remaining: Long): String {
        val minutes = remaining / 60_000
        val seconds = (remaining / 1000) % 60
        val clock = "${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"
        return t("$clock igjen", "$clock left")
    }

    fun layoutLabel(): String {
        val width = (window.innerWidth as Number).toInt()
        return when {
            width < 600 -> AdaptiveLayout.PHONE.label
            width < 1100 -> AdaptiveLayout.TABLET.label
            else -> t("Nettleser", "Browser")
        }
    }

    fun render() {
        // Keep focus, caret and scroll positions across the redraw.
        val active = document.activeElement
        val focusKey: String? = if (active != null && jsTypeOf(active.getAttribute) == "function") active.getAttribute("data-key") as String? else null
        val selStart = if (focusKey != null) active.selectionStart else null
        val selEnd = if (focusKey != null) active.selectionEnd else null
        val scrolls = mutableMapOf<String, Int>()
        val scrollNodes = document.querySelectorAll("[data-scroll-key]")
        for (i in 0 until (scrollNodes.length as Int)) {
            val n = scrollNodes[i]
            scrolls[n.getAttribute("data-scroll-key") as String] = (n.scrollLeft as Number).toInt()
        }
        val pageY = window.scrollY

        root.clear()
        document.documentElement.lang = I18n.language.htmlLang
        if (modernUi) document.body.classList.add("modern") else document.body.classList.remove("modern")
        val u = currentUser()
        if (u != null && openOwnerHomeOnce && cloudMode && cloud.isOwner) {
            openOwnerHomeOnce = false
            openOwnerPage()
        }
        applyTheme(u?.id)
        if (u == null) {
            if (user != null) user = null
            // Reload with a kept login: the same intro screen until Firebase has answered,
            // instead of flashing the login page.
            if (restoringCloud) renderRestoring(root) else renderLogin(root)
        } else {
            renderShell(root, u)
        }
        dialog?.let { renderDialog(root, it) }
        toastText?.let { root.div("toast", it).attr("role", "status") }

        val again = document.querySelectorAll("[data-scroll-key]")
        for (i in 0 until (again.length as Int)) {
            val n = again[i]
            val saved: Int? = scrolls[n.getAttribute("data-scroll-key") as String]
            if (saved != null) n.scrollLeft = saved
        }
        window.scrollTo(0, pageY)
        if (focusKey != null) {
            val target = document.querySelector("[data-key=\"" + focusKey + "\"]")
            if (target != null) {
                target.focus(js("({ preventScroll: true })"))
                if (selStart != null) runCatching { target.setSelectionRange(selStart, selEnd) }
            }
        }
    }

    private fun renderShell(root: El, u: UserAccount) {
        root.div("shell") {
            el("nav", "side-nav") {
                attr("aria-label", t("Hovedmeny", "Main menu"))
                div("side-brand", "Vareflow")
                Tab.entries.forEach { item -> navItem(item, "side-item") }
            }
            div("main-col") {
                el("header", "app-header") {
                    val m = cloud.membership
                    if (cloudMode && m != null) {
                        // Firebase: the company's logo and/or name, so people always see where they are.
                        val profile = cloud.company
                        val name = profile?.displayName ?: m.companyShortName.ifBlank { m.companyName }
                        val logo = cloud.logo
                        div("company-head") {
                            val showLogo = logo != null && (profile?.showsLogo(true) ?: true)
                            if (showLogo && logo != null) span("company-logo" + if (logo.darkBackground) " dark" else "") {
                                el("img") { attr("src", logoDataUrl(logo)); attr("alt", name) }
                            }
                            div("header-text") {
                                if (!showLogo || profile?.showsName(true) == true) div("brand company-name", name)
                                div("header-sub", u.name)
                            }
                        }
                    } else div("header-text") {
                        div("brand", "Vareflow")
                        div("header-sub", "${u.name} · ${layoutLabel()}")
                    }
                    if (cloudMode && cloud.isOwner) {
                        val unread = cloud.unreadSupportForOwner
                        el("button", "pill pill-owner" + if (ownerView) " active" else "", t("Eier", "Owner") + if (unread > 0) " · $unread" else "") {
                            attr("type", "button")
                            attr("title", t("Eier-siden: bedrifter og support", "Owner page: companies and support"))
                            onClick { openOwnerPage() }
                        }
                    }
                    if (cloudMode) {
                        val pending = cloud.pendingWrites
                        val tone = when (cloud.state) {
                            CloudState.ONLINE -> if (pending > 0) "warn" else "ok"
                            CloudState.ERROR -> "err"
                            else -> "warn"
                        }
                        el("button", "pill pill-cloud $tone", cloud.state.pill + if (pending > 0) " · $pending" else "") {
                            attr("type", "button")
                            attr("title", cloud.state.label + (cloud.lastError?.let { " – $it" } ?: ""))
                            onClick { showSyncDialog() }
                        }
                    } else {
                        span("pill pill-local", t("LOKAL", "LOCAL")).attr("title", t("Testmodus: data lagres bare i denne nettleseren", "Test mode: data is stored only in this browser"))
                    }
                    val helperUntil = if (cloudMode) cloud.membership?.accessUntil else null
                    if (helperUntil != null) {
                        span("pill pill-timer", helperPillText(helperUntil)).attr("id", "access-timer")
                            .attr("title", t("Tilgangen din varer til ${formatDateTime(helperUntil)}", "Your access lasts until ${formatDateTime(helperUntil)}"))
                    }
                    if (u.role == Role.EMPLOYEE && !cloudMode) {
                        span("pill pill-timer", accessText(((u.accessUntil ?: 0L) - nowMillis()).coerceAtLeast(0))).attr("id", "access-timer")
                    }
                }
                el("main", "content") {
                    attr("id", "main")
                    if (ownerView && cloudMode && cloud.isOwner) renderOwner(this, u) else when (tab) {
                        Tab.OVERVIEW -> renderOverview(this, u)
                        Tab.TELLING -> renderCounting(this, u)
                        Tab.ANALYSE -> renderAnalysePage(this, u)
                        Tab.PRODUCTS -> renderProducts(this, u)
                        Tab.MORE -> renderMore(this, u)
                    }
                }
            }
            el("nav", "bottom-nav") {
                attr("aria-label", t("Hovedmeny", "Main menu"))
                Tab.entries.forEach { item -> navItem(item, "bottom-item") }
            }
        }
    }

    private fun El.navItem(item: Tab, cls: String) {
        val activeTab = item == tab && !(ownerView && cloudMode && cloud.isOwner)
        el("button", cls + if (activeTab) " active" else "") {
            attr("type", "button")
            if (activeTab) attr("aria-current", "page")
            svg(navGlyph(item), "glyph")
            span("nav-label", item.label)
            if (item == Tab.MORE && cloudMode && cloud.unreadSupportForMe > 0) span("nav-dot").attr("aria-label", t("Nytt svar fra support", "New reply from support"))
            onClick { select(item) }
        }
    }

    private fun renderDialog(root: El, d: Dialog) {
        root.div("overlay") {
            on("click") { e -> if (e.target == node) closeDialog() }
            div("dialog") {
                attr("role", "dialog")
                attr("aria-modal", "true")
                h2(d.title, "dialog-title")
                d.build(this) { closeDialog() }
            }
        }
    }

    companion object {
        const val COUNT_SAVE_DEBOUNCE_MS = 600

        fun parseQuantity(text: String): Double? = text.replace(',', '.').toDoubleOrNull()?.takeIf { it >= 0.0 && !it.isNaN() }

        fun navGlyph(tab: Tab): String {
            val body = when (tab) {
                Tab.OVERVIEW -> """<path d="M4.8 11.5 12 4.8l7.2 6.7"/><rect x="6.7" y="10.6" width="10.6" height="8.6" rx="1.5"/>"""
                Tab.TELLING -> """<rect x="4" y="3.5" width="16" height="17" rx="3"/><path d="m8.6 12.5 2.4 2.4 5-5.3"/>"""
                Tab.ANALYSE -> """<path d="M4.3 18.7h15.4"/><rect x="5.8" y="12.5" width="2.9" height="6.2" rx=".6"/><rect x="10.6" y="9.4" width="2.9" height="9.3" rx=".6"/><rect x="15.4" y="5.8" width="2.9" height="12.9" rx=".6"/>"""
                Tab.PRODUCTS -> """<rect x="5.3" y="6.5" width="9.6" height="9.6" rx="1.6"/><rect x="11.5" y="11.5" width="7.2" height="7.2" rx="1.6"/>"""
                Tab.MORE -> """<path d="M4.8 7.2h14.4M4.8 12h14.4M4.8 16.8h14.4"/><circle cx="9.6" cy="7.2" r="2.1" fill="var(--nav)"/><circle cx="14.9" cy="12" r="2.1" fill="var(--nav)"/><circle cx="9.6" cy="16.8" r="2.1" fill="var(--nav)"/>"""
            }
            return """<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">$body</svg>"""
        }
    }
}

private const val LANGUAGE_KEY = "vareflow.language"

/** The language last used in this browser (the login page is drawn in it); Norwegian by default. */
fun loadLanguagePref(): AppLanguage =
    AppLanguage.fromCode(runCatching { window.localStorage.getItem(LANGUAGE_KEY) as String? }.getOrNull()) ?: AppLanguage.DEFAULT

fun saveLanguagePref(language: AppLanguage) {
    runCatching { window.localStorage.setItem(LANGUAGE_KEY, language.code) }
}

/** Same look as the intro in index.html (no entry animation, so redraws do not flicker). */
private fun renderRestoring(root: El) {
    root.div("boot still") {
        attr("role", "status")
        attr("aria-label", t("Kobler til …", "Connecting …"))
        div("boot-glass") {
            div("boot-brand", "Vareflow")
            div("boot-by", "by Garasjestudio")
            div("boot-bar") { span() }
        }
    }
}
